import type { PivotLayout, PivotMeasureFormat } from '@ztechapps/common.contract'
import {
  PivotAggregationEnum,
  PivotChartTypeEnum,
  PivotGrandTotalsEnum,
  PivotNegativeNumberFormatEnum,
  PivotTextAlignEnum,
} from '@ztechapps/common.contract'
import { PivotZoneEnum } from './PivotZoneEnum'

/** Empty pivot layout with totals shown and all nodes collapsed. */
export const EMPTY_PIVOT_LAYOUT: PivotLayout = {
  rows: [],
  columns: [],
  measures: [],
  filters: [],
  memberSorts: [],
  columnWidths: [],
  showSubtotals: true,
  showGrandTotals: PivotGrandTotalsEnum.On,
  expandAll: false,
}
/** Pivot body rows rendered above and below the rows in view. */
export const PIVOT_GRID_OVERSCAN = 10
/** Label shown for empty pivot member values. */
export const PIVOT_BLANK_LABEL = '(blank)'
/** Filter chip text when every member is kept. */
export const PIVOT_FILTER_ALL_TEXT = 'All'
/** Filter chip text after the kept-member count. */
export const PIVOT_FILTER_SELECTED_SUFFIX = 'selected'
/** Label of the pivot grand-total row and column. */
export const PIVOT_GRAND_TOTAL_LABEL = 'Grand Total'
/** Suffix appended to subtotal row labels. */
export const PIVOT_TOTAL_SUFFIX = 'Total'
/** Display label per pivot measure aggregation. */
export const PIVOT_AGGREGATION_LABELS: Record<PivotAggregationEnum, string> = {
  [PivotAggregationEnum.Sum]: 'Sum',
  [PivotAggregationEnum.Count]: 'Count',
  [PivotAggregationEnum.DistinctCount]: 'Distinct count',
  [PivotAggregationEnum.Average]: 'Average',
  [PivotAggregationEnum.Max]: 'Max',
  [PivotAggregationEnum.Min]: 'Min',
}
/** Display title per pivot zone — Measures reads Values in the dialog. */
export const PIVOT_ZONE_LABELS: Record<PivotZoneEnum, string> = {
  [PivotZoneEnum.Rows]: 'Rows',
  [PivotZoneEnum.Columns]: 'Columns',
  [PivotZoneEnum.Measures]: 'Values',
  [PivotZoneEnum.Filters]: 'Report filters',
}
/** Part label of a date field with no stored grouping. */
export const PIVOT_DAY_PART_LABEL = 'Day'
/** Max height of the scrollable pivot grid. */
export const PIVOT_GRID_MAX_HEIGHT = 'calc(100vh - var(--pivot-grid-chrome-height, 169px))'
/** Max height of the pivot grid while the pivot is fullscreen. */
export const PIVOT_FULLSCREEN_GRID_MAX_HEIGHT = 'calc(100vh - 80px)'
/** Inline styles for the printed pivot table. */
export const PIVOT_PRINT_STYLE = '<style>table{border-collapse:collapse}td{border:1px solid;padding:2px 6px}</style>'
/** Fallback display format for a pivot measure. */
export const PIVOT_DEFAULT_MEASURE_FORMAT: Required<PivotMeasureFormat> = {
  decimalPlaces: 0,
  currencySymbol: '',
  thousandsSeparator: ',',
  decimalSeparator: '.',
  negativeNumberFormat: PivotNegativeNumberFormatEnum.Minus,
  isPercent: false,
  textAlign: PivotTextAlignEnum.Right,
  nullValue: '',
}
/** Default decimal places for average measures. */
export const PIVOT_AVERAGE_DECIMAL_PLACES = 2
/** Max decimal places allowed in measure formatting. */
export const PIVOT_MAX_DECIMAL_PLACES = 10
/** Bootstrap alignment class per pivot text alignment. */
export const PIVOT_TEXT_ALIGN_CLASSES: Record<PivotTextAlignEnum, string> = {
  [PivotTextAlignEnum.Left]: 'text-start',
  [PivotTextAlignEnum.Center]: 'text-center',
  [PivotTextAlignEnum.Right]: 'text-end',
}
/** Display label per pivot view type. */
export const PIVOT_CHART_TYPE_LABELS: Record<PivotChartTypeEnum, string> = {
  [PivotChartTypeEnum.Grid]: 'Grid',
  [PivotChartTypeEnum.Column]: 'Column',
  [PivotChartTypeEnum.StackedColumn]: 'Stacked column',
  [PivotChartTypeEnum.Pie]: 'Pie',
  [PivotChartTypeEnum.ColumnLine]: 'Column line',
}
/** Icon name per pivot view type. */
export const PIVOT_CHART_TYPE_ICONS: Record<PivotChartTypeEnum, string> = {
  [PivotChartTypeEnum.Grid]: 'table',
  [PivotChartTypeEnum.Column]: 'chart-column',
  [PivotChartTypeEnum.StackedColumn]: 'chart-simple',
  [PivotChartTypeEnum.Pie]: 'chart-pie',
  [PivotChartTypeEnum.ColumnLine]: 'chart-line',
}
/** Series colours the chart cycles through, `--chart-series-1..N` in StatusColor.scss. */
export const PIVOT_CHART_SERIES_COUNT = 8
/** Min px width per category before a column chart scrolls sideways. */
export const PIVOT_CHART_CATEGORY_MIN_WIDTH = 72
/** Height the measure select and note take from the chart body. */
export const PIVOT_CHART_CONTROLS_HEIGHT = '56px'
